import { Grid, LinearProgress } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import { MeetingRoom } from "@material-ui/icons";
import React, { FC, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import {
  useCreateRoomMutation,
  useDeleteRoomMutation,
  useGetLocationQuery,
  useUpdateRoomMutation,
} from "../../api/rtk/LocationApi";
import { NewEntityId } from "../../data/constants";
import { RoomViewModel } from "../../model/RoomViewModel";
import { routesPaths } from "../../Routes";
import { navigateBack } from "../../utils/Navigation";
import DefaultButtons from "../common/buttons/DefaultButtons";
import TitleWithIcon from "../common/TitleWithIcon";
import RoomFields from "./RoomFields";

const initialRoom: RoomViewModel = {
  id: NewEntityId,
  name: "",
  floor: "",
  isPcRoom: true,
  numberOfSeats: 1,
};

const RoomForm: FC = () => {
  const history = useHistory();
  const { locationId, roomId } = useParams<{ locationId: string; roomId: string }>();

  const [room, setRoom] = useState(initialRoom);

  const { data: location, isFetching: isLocationFetching } = useGetLocationQuery(locationId);
  const [deleteRoom, { isLoading: isDeleteRoomLoading }] = useDeleteRoomMutation();
  const [createRoom, { isLoading: isCreateRoomLoading }] = useCreateRoomMutation();
  const [updateRoom, { isLoading: isUpdateRoomLoading }] = useUpdateRoomMutation();
  const isLoading = isLocationFetching || isDeleteRoomLoading || isCreateRoomLoading || isUpdateRoomLoading;

  useEffect(() => {
    if (location !== undefined) {
      const room = location.rooms.find((room) => room.id === roomId);
      setRoom(room !== undefined ? room : initialRoom);
    }
  }, [roomId, location]);

  if (isLoading) {
    return <LinearProgress variant="query" />;
  }

  if (location === undefined) {
    return <></>;
  }

  const close = () => navigateBack(routesPaths.location(locationId), history, history.location);

  const handleChange = (partialRoom: Partial<RoomViewModel>) =>
    setRoom((prevRoom) => ({ ...prevRoom, ...partialRoom }));

  const handleClose = () => close();

  const handleSave = async () => {
    var requestData = { locationId, data: room };
    const response = await (room.id === NewEntityId ? createRoom(requestData) : updateRoom(requestData)).unwrap();
    if (response.success) {
      close();
    }
  };

  const handleDelete = async () => {
    const response = await deleteRoom({ locationId, roomId }).unwrap();
    if (response.success) {
      close();
    }
  };

  const isValid = () => room.name.length > 0 && room.numberOfSeats > 0;

  return (
    <Grid container={true} direction="column" spacing={3}>
      <Grid item={true} container={true} spacing={0} alignItems="baseline">
        <Grid item={true} xs={12} sm={6}>
          <Typography variant="h6" component="span">
            {location.name}
          </Typography>
        </Grid>
        <Grid item={true} xs={12} sm={6}>
          <DefaultButtons
            onClose={handleClose}
            onDelete={handleDelete}
            onSave={handleSave}
            isSaveButtonEnabled={isValid()}
            isDeleteButtonEnabled={room.id !== NewEntityId}
          />
        </Grid>
      </Grid>
      <Grid item={true}>
        <TitleWithIcon title={room.name} variant="h4">
          <MeetingRoom />
        </TitleWithIcon>
      </Grid>
      <Grid item={true}>
        <RoomFields room={room} onPropertyValueChanges={handleChange} />
      </Grid>
    </Grid>
  );
};

export default RoomForm;
